import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

interface PaymentTransaction {
  id: string;
  booking_id: string;
  user_id: string;
  amount: number;
  payment_method: 'orange_money' | 'myzaka' | 'fnb' | 'cash';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'disputed';
  transaction_reference: string;
  created_at: string;
  updated_at: string;
  users: {
    full_name: string;
    email: string;
  };
  bookings: {
    start_time: string;
    end_time: string;
    lockers: {
      locker_number: string;
      locations: {
        name: string;
      };
    };
  };
}

const AdminPaymentManagement: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedMethod, setSelectedMethod] = useState<string>('all');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransaction | null>(null);
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchTransactions();
    }
  }, [user, selectedStatus, selectedMethod]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/payments/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      } else {
        console.error('Payments API error:', response.status);
        toast.error('Failed to load payment data');
        // Fallback to mock data
        setMockTransactions();
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Network error - using sample data');
      // Fallback to mock data on error
      setMockTransactions();
    } finally {
      setIsLoading(false);
    }
  };

  const setMockTransactions = () => {
    const mockData: PaymentTransaction[] = [
      {
        id: '1',
        booking_id: 'b001',
        user_id: 'u001',
        amount: 25.00,
        payment_method: 'orange_money',
        status: 'completed',
        transaction_reference: 'OM_20241201_001',
        created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        users: {
          full_name: 'John Doe',
          email: 'john.doe@email.com'
        },
        bookings: {
          start_time: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          end_time: new Date(Date.now() + 1000 * 60 * 60 * 2).toISOString(),
          lockers: {
            locker_number: 'A12',
            locations: {
              name: 'Main Mall'
            }
          }
        }
      },
      {
        id: '2',
        booking_id: 'b002',
        user_id: 'u002',
        amount: 35.00,
        payment_method: 'myzaka',
        status: 'pending',
        transaction_reference: 'MZ_20241201_002',
        created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
        users: {
          full_name: 'Jane Smith',
          email: 'jane.smith@email.com'
        },
        bookings: {
          start_time: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
          end_time: new Date(Date.now() + 1000 * 60 * 60 * 3).toISOString(),
          lockers: {
            locker_number: 'B8',
            locations: {
              name: 'Riverwalk Mall'
            }
          }
        }
      },
      {
        id: '3',
        booking_id: 'b003',
        user_id: 'u003',
        amount: 15.00,
        payment_method: 'cash',
        status: 'completed',
        transaction_reference: 'CASH_20241201_003',
        created_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        users: {
          full_name: 'Bob Johnson',
          email: 'bob.johnson@email.com'
        },
        bookings: {
          start_time: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          end_time: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          lockers: {
            locker_number: 'C15',
            locations: {
              name: 'Main Mall Bus Rank'
            }
          }
        }
      },
      {
        id: '4',
        booking_id: 'b004',
        user_id: 'u004',
        amount: 50.00,
        payment_method: 'fnb',
        status: 'failed',
        transaction_reference: 'FNB_20241201_004',
        created_at: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        users: {
          full_name: 'Alice Brown',
          email: 'alice.brown@email.com'
        },
        bookings: {
          start_time: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
          end_time: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          lockers: {
            locker_number: 'A20',
            locations: {
              name: 'Main Mall'
            }
          }
        }
      }
    ];
    setTransactions(mockData);
  };

  const handleRefund = async () => {
    if (!selectedTransaction || !refundAmount || !refundReason) {
      toast.error('Please fill in all fields');
      return;
    }

    const amount = parseFloat(refundAmount);
    if (amount <= 0 || amount > selectedTransaction.amount) {
      toast.error('Invalid refund amount');
      return;
    }

    try {
      // Mock refund processing
      toast.success(`Refund of BWP ${amount} processed successfully`);
      setShowRefundModal(false);
      setSelectedTransaction(null);
      setRefundAmount('');
      setRefundReason('');

      // Update the transaction status in the list
      setTransactions(prev => prev.map(t =>
        t.id === selectedTransaction.id
          ? { ...t, status: 'refunded' as const, updated_at: new Date().toISOString() }
          : t
      ));
    } catch (error) {
      toast.error('Failed to process refund');
    }
  };

  const handleStatusUpdate = async (transactionId: string, newStatus: string) => {
    try {
      // Mock status update
      toast.success(`Transaction status updated to ${newStatus}`);
      setTransactions(prev => prev.map(t =>
        t.id === transactionId
          ? { ...t, status: newStatus as any, updated_at: new Date().toISOString() }
          : t
      ));
    } catch (error) {
      toast.error('Failed to update transaction status');
    }
  };

  const openRefundModal = (transaction: PaymentTransaction) => {
    setSelectedTransaction(transaction);
    setRefundAmount(transaction.amount.toString());
    setRefundReason('');
    setShowRefundModal(true);
  };

  const formatCurrency = (amount: number) => {
    return `BWP ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-blue-100 text-blue-800';
      case 'disputed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'orange_money': return 'bg-orange-100 text-orange-800';
      case 'myzaka': return 'bg-purple-100 text-purple-800';
      case 'fnb': return 'bg-blue-100 text-blue-800';
      case 'cash': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusActions = (transaction: PaymentTransaction) => {
    switch (transaction.status) {
      case 'pending':
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleStatusUpdate(transaction.id, 'completed')}
              className="text-green-600 hover:text-green-900 text-sm"
            >
              Mark Complete
            </button>
            <button
              onClick={() => handleStatusUpdate(transaction.id, 'failed')}
              className="text-red-600 hover:text-red-900 text-sm"
            >
              Mark Failed
            </button>
          </div>
        );
      case 'completed':
        return (
          <button
            onClick={() => openRefundModal(transaction)}
            className="text-blue-600 hover:text-blue-900 text-sm"
          >
            Process Refund
          </button>
        );
      case 'failed':
        return (
          <button
            onClick={() => handleStatusUpdate(transaction.id, 'pending')}
            className="text-yellow-600 hover:text-yellow-900 text-sm"
          >
            Retry
          </button>
        );
      default:
        return <span className="text-gray-500 text-sm">No actions</span>;
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const statusMatch = selectedStatus === 'all' || transaction.status === selectedStatus;
    const methodMatch = selectedMethod === 'all' || transaction.payment_method === selectedMethod;
    return statusMatch && methodMatch;
  });

  const totalRevenue = filteredTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);

  const pendingAmount = filteredTransactions
    .filter(t => t.status === 'pending')
    .reduce((sum, t) => sum + t.amount, 0);

  if (user?.role !== 'admin') {
    return <div className="p-6">Access denied. Admin privileges required.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Payment Management</h1>
        <div className="text-sm text-gray-600">
          Total Transactions: {filteredTransactions.length}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(pendingAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredTransactions.length > 0
                  ? `${((filteredTransactions.filter(t => t.status === 'completed').length / filteredTransactions.length) * 100).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="disputed">Disputed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Methods</option>
              <option value="orange_money">Orange Money</option>
              <option value="myzaka">MyZaka</option>
              <option value="fnb">FNB</option>
              <option value="cash">Cash</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Booking Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Payment Info
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTransactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {transaction.users.full_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {transaction.users.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {transaction.bookings.lockers.locations.name} - Locker {transaction.bookings.lockers.locker_number}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(transaction.bookings.start_time)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMethodColor(transaction.payment_method)}`}>
                        {transaction.payment_method.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Ref: {transaction.transaction_reference}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                    {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatDate(transaction.updated_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {getStatusActions(transaction)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Refund Modal */}
      {showRefundModal && selectedTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Process Refund
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Amount
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedTransaction.amount}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter refund amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Reason
                  </label>
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter reason for refund"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefund}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Process Refund
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentManagement;
